/**
 * Shared zod schemas for API response boundary validation.
 *
 * Bug-hunt cross-cutting #2 + items #11/#12/#13/#24: every external
 * response should run through a typed schema so shape drift becomes a
 * typed `ResponseShapeError` rather than a silent `?? []` coercion
 * (which papers over `null`, missing fields, or completely wrong shapes
 * with an empty-list UI).
 *
 * The schemas here cover the highest-traffic / most-error-prone
 * response shapes. As individual API files migrate from `??` /
 * `.forEach` patterns to `parseResponse(Schema, response.data)`, they
 * should pick up the relevant schema from here.
 */

import { z } from 'zod';

/**
 * The canonical paginated-list envelope. Used by every Django REST
 * Framework list endpoint and most FastAPI list endpoints that paginate.
 *
 * - `results` is REQUIRED to be an array; `null` and missing throw.
 *   This was the most common §2 trap: `response.data.results ?? []`
 *   silently coerced a backend bug (`results: null`) into an empty
 *   list, hiding the real error from the UI.
 */
export function paginatedResponse<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    count: z.number().int().nonnegative(),
    next: z.union([z.string(), z.null()]).optional(),
    previous: z.union([z.string(), z.null()]).optional(),
    results: z.array(item),
  });
}

/**
 * RFC 7807 problem-details shape, used by error responses.
 * Bug-hunt #12: 403 bodies were being run through camelCase conversion
 * even though their shape isn't guaranteed snake_case. Parse with this
 * schema first; surface `title`/`detail` to the UI directly.
 */
export const ProblemDetailsSchema = z.object({
  type: z.string().optional(),
  title: z.string().optional(),
  status: z.number().int().optional(),
  detail: z.union([z.string(), z.record(z.string(), z.unknown())]).optional(),
  instance: z.string().optional(),
});

export type ProblemDetails = z.infer<typeof ProblemDetailsSchema>;

/**
 * Minimal user identity — just enough fields that any auth-adjacent
 * endpoint can validate it returned a sensible object. Real per-call
 * schemas should extend this with their specific fields.
 */
export const UserSummarySchema = z.object({
  id: z.number().int(),
  email: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
});

/**
 * Lightweight organization/network identity for context-switcher
 * use cases. Per-call schemas can intersect with this.
 */
export const ContextEntitySchema = z.object({
  id: z.number().int(),
  name: z.string(),
  identifier: z.string().optional(),
});
